<template>
  <Layout>
    <PageHeader :title="title">
      <template #afterTitle>
        <McButtonBack :data-me="`${dataMePrefix}-btn-back`" />
      </template>
    </PageHeader>
    <b-overlay
      class="inspection-detail__overlay"
      :show="isLoading"
      rounded="sm"
      opacity="0.6"
    >
    <div v-if="inspection" class="inspection-detail">
      <section class="inspection-detail__section">
        <h4 class="card-title">
          {{ sectionLabels.information }}
        </h4>
        <b-card body-class="inspection-detail__information-body">
          <ReadOnly :items="informationItems" />
        </b-card>
      </section>

      <section class="inspection-detail__section">
        <h4 class="card-title">
          {{ sectionLabels.resultSummary }}
        </h4>
        <b-row class="inspection-detail__summary-row">
          <b-col
            v-for="summary in summaries"
            :key="summary.label"
            cols="12"
            sm="6"
            lg="3"
          >
            <b-card
              :class="[
                'inspection-detail__summary-card',
                { 'inspection-detail__summary-card--active': isConditionActive(summary.condition) },
              ]"
              body-class="p-3"
              role="button"
              tabindex="0"
              :data-me="`${dataMePrefix}-btn-filter-${summary.condition}`"
              @click="selectCondition(summary.condition)"
              @keydown.enter.prevent="selectCondition(summary.condition)"
              @keydown.space.prevent="selectCondition(summary.condition)"
            >
              <strong
                :class="[
                  'inspection-detail__summary-value',
                  summary.variant && `text-${summary.variant}`,
                ]"
              >
                {{ summary.value }}
              </strong>
              <div class="inspection-detail__summary-label">
                {{ summary.label }}
              </div>
            </b-card>
          </b-col>
        </b-row>
        <McAlert
          v-if="unhealthyQuestionCount"
          class="inspection-detail__finding-alert"
          variant="error"
          withIcon
          :text="findingAlertText"
        />
        <McInlineFilter
          class="inspection-detail__condition-tabs"
          :data-me="`${dataMePrefix}-filter-condition`"
          :filterItems="conditionFilterItems"
          :activeKeys="activeConditionKeys"
          @update:activeKeys="handleConditionFilter"
        />
      </section>

      <section class="inspection-detail__categories">
        <b-card
          v-for="(category, categoryIndex) in displayedCategories"
          :key="category.id"
          class="inspection-detail__category"
          no-body
        >
          <button
            :data-me="`${dataMePrefix}-btn-toggle-category-${category.id}`"
            type="button"
            class="inspection-detail__category-header"
            :aria-expanded="category.isExpanded"
            :aria-controls="`inspection-category-${categoryIndex}`"
            @click="toggleCategory(category.id)"
          >
            <span>{{ category.name }}</span>
            <span class="inspection-detail__category-action">
              <McBadge
                :text="categoryBadgeText(category)"
                variant="outlineSecondary"
                size="medium"
                type="button"
              />
              <i
                :class="[
                  'mc-icon',
                  category.isExpanded
                    ? 'icon-outline_chevron-up'
                    : 'icon-outline_chevron-down',
                ]"
              ></i>
            </span>
          </button>

          <b-collapse
            :id="`inspection-category-${categoryIndex}`"
            :visible="category.isExpanded"
          >
            <div class="inspection-detail__category-body">
              <div class="inspection-detail__question-label">
                {{ sectionLabels.question }}
              </div>
              <div
                v-for="(question, questionIndex) in category.questions"
                :key="question.id"
                class="inspection-detail__question"
              >
                <div class="inspection-detail__question-header">
                  <div class="inspection-detail__question-content">
                    <span class="inspection-detail__question-number">
                      {{ questionIndex + 1 }}.
                    </span>
                    <span>
                      {{ question.title }}
                      <span v-if="question.isCritical" class="text-danger">*</span>
                    </span>
                  </div>
                  <McBadge
                    :text="answerText(question)"
                    :variant="answerBadgeVariant(question)"
                    size="medium"
                    type="button"
                  />
                </div>

                <div
                  v-if="hasSupportingEvidence(question)"
                  class="inspection-detail__finding"
                >
                  <div v-if="question.attachments && question.attachments.length">
                    <div class="inspection-detail__finding-label">
                      {{ sectionLabels.supportingEvidence }}
                    </div>
                    <div v-if="question.note" class="inspection-detail__note">
                      <span>{{ sectionLabels.note }}:</span> {{ question.note }}
                    </div>
                    <div class="inspection-detail__attachments">
                      <div
                        v-for="(attachment, attachmentIndex) in question.attachments"
                        :key="attachment.id"
                        class="inspection-detail__attachment"
                      >
                        <img
                          :data-me="`${dataMePrefix}-btn-preview-evidence-${attachment.id}`"
                          :src="attachment.url"
                          :alt="attachment.name"
                          @click="openImagePreview(question.attachments, attachmentIndex)"
                        />
                        <button
                          :data-me="`${dataMePrefix}-btn-download-evidence-${attachment.id}`"
                          type="button"
                          class="inspection-detail__attachment-download"
                          @click="handleDownloadImage(attachment.url, attachment.name)"
                        >
                          <i class="mc-icon icon-outline_download"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else-if="question.note"
                    class="inspection-detail__note"
                  >
                    <span>{{ sectionLabels.note }}:</span> {{ question.note }}
                  </div>
                </div>
              </div>
            </div>
          </b-collapse>
        </b-card>
        <McEmptyState
          v-if="!displayedCategories.length"
          :title="$t('vehicleInspection.emptyState.title')"
          :desc="$t('vehicleInspection.emptyState.description')"
          :hasButton="false"
          size="sm"
        />
      </section>
    </div>
    <McEmptyState
      v-else-if="!isLoading"
      :title="$t('vehicleInspection.emptyState.title')"
      :desc="$t('vehicleInspection.emptyState.description')"
      :hasButton="false"
      size="sm"
    />
    </b-overlay>

    <McShowImage
      :data-me="`${dataMePrefix}-image-preview`"
      :visible.sync="imagePreview.visible"
      :attachments="imagePreview.attachments"
      :activeIndex.sync="imagePreview.activeIndex"
      @download="handleDownloadImage"
    />
  </Layout>
</template>

<script>
import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McButtonBack from '@/components/McButtonBack'
import ReadOnly from './components/ReadOnly.vue'
import { getVehicleInspectionTemplateRoute } from './lib/vehicleInspectionAdapter'
import McBadge from '@/components/McBadge'
import McAlert from '@/components/McAlert'
import McInlineFilter from '@/components/McInlineFilter'
import McEmptyState from '@/components/McEmptyState'
import McShowImage from '@/components/McShowImage'
import { GET_VEHICLE_INSPECTION } from '@/services/api/vehicleInspection'
import {
  buildVehicleInspectionInput,
  normalizeVehicleInspectionResponse,
} from './lib/vehicleInspectionAdapter'
import { mapGetters } from 'vuex'

export default {
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    ReadOnly,
    McBadge,
    McAlert,
    McInlineFilter,
    McEmptyState,
    McShowImage,
  },
  data() {
    return {
      dataMePrefix: 'vehicle-inspection-detail',
      inspection: null,
      isLoading: false,
      activeConditionKeys: [],
      imagePreview: {
        visible: false,
        attachments: [],
        activeIndex: 0,
      },
      categories: [],
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    canUpdateTemplate() {
      return this.hasAccess(['c19a-default-vehicle-inspection:update'])
    },
    sectionLabels() {
      return {
        information: this.$t('vehicleInspection.detail.mainInformation'),
        resultSummary: this.$t('vehicleInspection.detail.resultSummary'),
        question: this.$t('vehicleInspection.detail.question'),
        emptyAnswer: this.$t('vehicleInspection.detail.emptyAnswer'),
        supportingEvidence: this.$t('vehicleInspection.detail.supportingEvidence'),
        note: this.$t('vehicleInspection.detail.note'),
      }
    },
    informationItems() {
      return [
        {
          key: 'result',
          label: this.$t('vehicleInspection.field.result'),
          value: this.inspection.healthyState
            ? this.$t('vehicleInspection.status.eligible')
            : this.$t('vehicleInspection.status.notEligible'),
          status: this.inspection.healthyState ? 'success' : 'danger',
        },
        {
          key: 'vehicle',
          label: this.$t('vehicleInspection.field.vehicle'),
          value: this.inspection.submittedLicensePlate,
        },
        {
          key: 'inspector',
          label: this.$t('vehicleInspection.field.inspectedBy'),
          value: this.inspection.inspectorName,
        },
        {
          key: 'submittedAt',
          label: this.$t('vehicleInspection.field.submittedOn'),
          value: this.formatInspectionDate(this.inspection.submittedAt),
        },
        {
          key: 'templateName',
          label: this.$t('vehicleInspection.field.templateName'),
          value: this.inspection.templateName,
          to: getVehicleInspectionTemplateRoute(this.inspection, {
            canUpdate: this.canUpdateTemplate,
          }),
          dataMe: `${this.dataMePrefix}-link-template`,
        },
      ]
    },
    title() {
      return this.$t('vehicleInspection.detail.title', {
        documentNumber: this.inspection?.documentNumber ?? this.$route.params.id,
      })
    },
    questions() {
      return this.categories.reduce((result, category) => {
        return result.concat(category.questions)
      }, [])
    },
    summaries() {
      return [
        {
          label: this.$t('vehicleInspection.summary.totalQuestions'),
          value: this.inspection.stats.totalQuestion,
          condition: 'all',
        },
        {
          label: this.$t('vehicleInspection.status.healthy'),
          value: this.inspection.stats.healthy,
          variant: 'success',
          condition: 'healthy',
        },
        {
          label: this.$t('vehicleInspection.status.unhealthy'),
          value: this.inspection.stats.unhealthy,
          variant: 'danger',
          condition: 'unhealthy',
        },
        {
          label: this.$t('vehicleInspection.status.notFilled'),
          value: this.inspection.stats.emptyAnswer,
          variant: 'secondary',
          condition: 'notFilled',
        },
      ]
    },
    conditionFilterItems() {
      return [
        { id: 'all', label: 'vehicleInspection.status.all' },
        { id: 'healthy', label: 'vehicleInspection.status.healthy' },
        { id: 'unhealthy', label: 'vehicleInspection.status.unhealthy' },
        { id: 'notFilled', label: 'vehicleInspection.status.notFilled' },
      ]
    },
    unhealthyQuestionCount() {
      return this.inspection.stats.unhealthy
    },
    findingAlertText() {
      return this.$t('vehicleInspection.detail.findingAlert', {
        count: this.unhealthyQuestionCount,
      })
    },
    displayedCategories() {
      return this.categories
        .map((category) => {
          const questions = category.questions
            .filter((question) => this.matchesActiveCondition(question))
            .sort((firstQuestion, secondQuestion) => {
              return this.questionSortWeight(firstQuestion) - this.questionSortWeight(secondQuestion)
            })

          return { ...category, questions }
        })
        .filter((category) => category.questions.length)
        .sort((firstCategory, secondCategory) => {
          return this.categoryFindingCount(secondCategory) - this.categoryFindingCount(firstCategory)
        })
    },
  },
  watch: {
    '$route.params.id': 'getInspection',
    '$route.query.condition'(condition) {
      this.activeConditionKeys = this.conditionKeysFromRoute(condition)
    },
  },
  created() {
    this.getInspection()
  },
  methods: {
    async getInspection() {
      const inspectionId = Number(this.$route.params.id)
      this.inspection = null
      this.categories = []
      this.activeConditionKeys = this.conditionKeysFromRoute(this.$route.query.condition)
      this.isLoading = false

      if (!Number.isInteger(inspectionId) || inspectionId <= 0) return

      this.isLoading = true
      try {
        const response = await this.$apollo.query({
          query: GET_VEHICLE_INSPECTION,
          fetchPolicy: 'no-cache',
          variables: { filter: buildVehicleInspectionInput(inspectionId) },
        })
        const inspection = normalizeVehicleInspectionResponse(response.data)

        this.inspection = inspection
        this.categories = inspection?.categories ?? []
      } catch (_error) {
        this.inspection = null
      } finally {
        this.isLoading = false
      }
    },
    formatInspectionDate(value) {
      if (!value) return '-'

      const locale = this.$i18n.locale === 'en' ? 'en-US' : 'id-ID'
      const date = new Date(value)
      const formattedDate = new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(date)
      const formattedTime = new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date)

      return `${formattedDate}, ${formattedTime} WIB`
    },
    selectCondition(condition) {
      this.activeConditionKeys = condition === 'all' ? [] : [condition]
    },
    conditionKeysFromRoute(condition) {
      return ['healthy', 'unhealthy', 'notFilled'].includes(condition) ? [condition] : []
    },
    isConditionActive(condition) {
      return (this.activeConditionKeys[0] || 'all') === condition
    },
    handleConditionFilter(activeKeys) {
      this.activeConditionKeys = activeKeys
    },
    matchesActiveCondition(question) {
      const activeCondition = this.activeConditionKeys[0] || 'all'

      if (activeCondition === 'healthy') return question.hasAnswer && question.isHealthy === true
      if (activeCondition === 'unhealthy') return question.hasAnswer && question.isHealthy === false
      if (activeCondition === 'notFilled') return !question.hasAnswer

      return true
    },
    openImagePreview(attachments, activeIndex) {
      this.imagePreview = {
        visible: true,
        attachments,
        activeIndex,
      }
    },
    async handleDownloadImage(url, fileName) {
      try {
        const response = await fetch(url)
        const blobUrl = window.URL.createObjectURL(await response.blob())
        const link = document.createElement('a')

        link.href = blobUrl
        link.download = fileName || 'download'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
      } catch (_error) {
        const link = document.createElement('a')

        link.href = url
        link.target = '_blank'
        link.download = fileName || 'download'
        link.click()
      }
    },
    hasSupportingEvidence(question) {
      if (!question.hasAnswer) return false

      return Boolean(question.note || (question.attachments && question.attachments.length))
    },
    answerBadgeVariant(question) {
      if (!question.hasAnswer) return 'outlinePlainSecondary'
      if (question.isHealthy === null) return 'outlineSecondary'

      return question.isHealthy ? 'outlineSuccess' : 'outlineDanger'
    },
    answerText(question) {
      if (!question.hasAnswer) return this.sectionLabels.emptyAnswer
      if (question.isHealthyValue !== '') return question.isHealthyValue
      if (question.textValue !== '') return question.textValue
      if (question.isHealthy === null) {
        return this.$t('vehicleInspection.detail.photoAnswer', {
          count: question.totalPhoto,
        })
      }

      return question.isHealthy
        ? this.$t('vehicleInspection.status.healthy')
        : this.$t('vehicleInspection.status.unhealthy')
    },
    questionCountLabel(questionCount) {
      return this.$t('vehicleInspection.detail.questionCount', { count: questionCount })
    },
    categoryFindingCount(category) {
      return category.questions.filter(
        (question) => question.hasAnswer && question.isHealthy === false,
      ).length
    },
    categoryBadgeText(category) {
      return this.questionCountLabel(category.questions.length)
    },
    questionSortWeight(question) {
      if (!question.hasAnswer) return 2
      return question.isHealthy === false ? 0 : 1
    },
    toggleCategory(categoryId) {
      this.categories = this.categories.map((category) => ({
        ...category,
        isExpanded: category.id === categoryId ? !category.isExpanded : false,
      }))
    },
  },
}
</script>

<style lang="scss" scoped>
.inspection-detail {
  display: grid;
  gap: 20px;

  &__section {
    min-width: 0;
  }

  &__overlay {
    min-height: 160px;
  }

  &__information-body {
    padding: 16px;
  }

  &__summary-row {
    margin-bottom: 16px;

    > [class*='col-'] {
      margin-bottom: 12px;
    }
  }

  &__summary-card {
    height: 100%;
    margin-bottom: 0;
    border-radius: 8px;
    cursor: pointer;
  }

  &__summary-value {
    display: block;
    margin-bottom: 8px;
    color: $mep-color-text;
    font-size: 24px;
    line-height: 1;
  }

  &__summary-label {
    display: flex;
    align-items: center;
    gap: 4px;
    color: $mep-color-text-200;
    font-size: 14px;
  }

  &__condition-tabs {
    margin-bottom: 16px;
  }

  &__finding-alert {
    margin-bottom: 16px;
  }

  &__categories {
    display: grid;
    gap: 12px;
  }

  &__category {
    overflow: hidden;
    margin-bottom: 0;
  }

  &__category-header {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border: 0;
    background: $white;
    color: $mep-color-text;
    font-weight: 600;
    text-align: left;
  }

  &__category-action {
    display: flex;
    align-items: center;
    gap: 16px;
    color: $mep-color-text-300;
  }

  &__category-body {
    padding: 0 16px 16px;
    border-top: 1px solid $gray-400;
  }

  &__question-label {
    padding: 14px 0 10px;
    color: $mep-color-text-300;
    font-size: 12px;
    font-weight: 500;
  }

  &__question {
    padding: 13px 14px;
    border: 1px solid $gray-400;
    border-radius: 6px;
    color: $mep-color-text-400;

    & + & {
      margin-top: 10px;
    }
  }

  &__question-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  &__question-content {
    display: flex;
    min-width: 0;
    gap: 14px;
  }

  &__question-number {
    flex-shrink: 0;
  }

  &__finding {
    display: grid;
    gap: 8px;
    margin: 10px 0 0 28px;
    padding-top: 10px;
    border-top: 1px solid $gray-400;
    font-size: 12px;
  }

  &__finding-label {
    margin-bottom: 8px;
    color: $mep-color-text-400;
    font-weight: 500;
  }

  &__attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }

  &__attachment {
    position: relative;
    display: block;
    width: 64px;
    height: 48px;
    overflow: hidden;
    border: 1px solid $gray-400;
    border-radius: 4px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      cursor: pointer;
    }
  }

  &__attachment-download {
    position: absolute;
    top: 3px;
    right: 3px;
    display: inline-flex;
    width: 18px;
    height: 18px;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    border: 0;
    background: $white;
    color: $primary;
    font-size: 11px;
  }

  &__note {
    color: $mep-color-text-300;

    span {
      color: $mep-color-text-400;
      font-weight: 500;
    }
  }

}

@media (max-width: 575.98px) {
  .inspection-detail {
    &__question {
      padding: 12px;
    }

    &__question-header {
      align-items: flex-start;
    }

    &__finding {
      margin-left: 0;
    }

    &__category-action {
      gap: 8px;
    }
  }
}
</style>
